/* eslint-disable react/display-name, @typescript-eslint/no-empty-function */
/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-12-29 16:11:57
 */
import React, { useState, useRef, RefObject } from 'react'
import { Checkbox, Empty, CheckboxProps } from 'antd'
import { AutoSizer, Grid, GridCellRenderer } from 'react-virtualized'
import { setLoading } from '../../../actions/main'
import { setPhotos, deletePhoto, fetchMorePhotos } from '../../../actions/photo'
import actionTypes from '../../../constants/actionTypes'
import CONFIG from '../../../constants/config'
import { buildFetchPhotosParams } from '@/pages/components/photoLib/photo'
import Viewer, { ViewerRef } from '../../components/ViewerV2'
import Photo from '../../components/Photo'
import backTop from '../../../images/back_top.png'
import { capturePromiseException } from '../../../utils/sentryUtils'
import { formatNum } from '../../../utils/utils'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'

interface PhotosListProps {
  photoListRef: RefObject<Grid>
  canFavorite?: boolean
  handleSensorOfClickEvent: (
    clickItem: string,
    restProperties?: Record<string, any>
  ) => void
}

interface PhotoItem {
  seq: number
  [key: string]: any
}

function PhotosList(props: PhotosListProps) {
  const { photoListRef, canFavorite, handleSensorOfClickEvent } = props

  const dispatch = useAppDispatch()
  // 直接访问属性，不使用 .get()
  const queryFilter = useAppSelector((state) => state.photo.queryFilter)
  const photos = useAppSelector((state) => state.photo.photos)
  const photosNum = useAppSelector((state) => state.photo.photosNum)
  const checkedPhotos = useAppSelector((state) => state.photo.checkedPhotos)
  const unCheckedPhotos = useAppSelector((state) => state.photo.unCheckedPhotos)
  const checkAll = useAppSelector((state) => state.photo.checkAll)
  const lastPageCond = useAppSelector((state) => state.photo.lastPageCond)
  const groupId = useAppSelector((state) => state.group.groupId)
  const myRole = useAppSelector((state) => state.group.myRole)
  const userId = useAppSelector((state) => state.main.user?.user_id)

  const [autoSizerWidth, setAutoSizerWidth] = useState(0)
  const [visibleBackTop, setVisibleBackTop] = useState(false)

  const scrollFetchingRef = useRef(false)
  const viewerRef = useRef<ViewerRef>(null)

  const handlePushCheckedPhoto = (photo: PhotoItem) => {
    if (checkAll) {
      dispatch({
        type: actionTypes.REMOVE_UNCHECKED_PHOTO,
        data: photo
      })
    } else {
      if (Object.keys(checkedPhotos).length === photosNum - 1) {
        dispatch({
          type: actionTypes.SET_CHECK_ALL,
          data: true
        })
        dispatch({ type: actionTypes.CLEAR_CHECKED_PHOTO })
        dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })
      } else {
        dispatch({
          type: actionTypes.PUSH_CHECKED_PHOTO,
          data: photo
        })
      }
    }
  }

  const handleRemoveCheckedPhoto = (photo: PhotoItem) => {
    if (checkAll) {
      if (Object.keys(unCheckedPhotos).length === photosNum - 1) {
        dispatch({
          type: actionTypes.SET_CHECK_ALL,
          data: false
        })
        dispatch({ type: actionTypes.CLEAR_CHECKED_PHOTO })
        dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })
      } else {
        dispatch({
          type: actionTypes.PUSH_UNCHECKED_PHOTO,
          data: photo
        })
      }
    } else {
      dispatch({
        type: actionTypes.REMOVE_CHECKED_PHOTO,
        data: photo
      })
    }
  }

  const handleGridScroll = ({
    clientHeight,
    scrollHeight,
    scrollTop
  }: {
    clientHeight: number
    scrollHeight: number
    scrollTop: number
  }) => {
    if (scrollTop > window.screen.height - 210) {
      setVisibleBackTop(true)
    } else {
      setVisibleBackTop(false)
    }

    if (
      !scrollFetchingRef.current &&
      scrollHeight > clientHeight &&
      scrollHeight - clientHeight - scrollTop <= 0
    ) {
      handleFetchMore()
    }
  }

  const handleBackTopClick = () => {
    const dom = document.getElementsByClassName('virtualized-grid')[0]
    dom.scrollTop = 0
  }

  const handleFetchMore = () => {
    if (lastPageCond && queryFilter) {
      if (!scrollFetchingRef.current) {
        scrollFetchingRef.current = true

        let filter = queryFilter as any
        if (
          myRole !== CONFIG.memberRoleTypes.ADMIN &&
          myRole !== CONFIG.memberRoleTypes.MASTER_ADMIN
        ) {
          filter = { ...filter, departmentUsers: [{ userID: userId }] }
        }

        dispatch(
          fetchMorePhotos(buildFetchPhotosParams(filter, groupId, lastPageCond))
        )
          .then((results) => {
            scrollFetchingRef.current = false
          })
          .catch(capturePromiseException)
      }
    }
  }

  const renderCell = ({
    autoSizerWidth,
    columnIndex,
    key,
    rowIndex,
    style
  }: {
    autoSizerWidth: number
    columnIndex: number
    key: string
    rowIndex: number
    style: React.CSSProperties
  }) => {
    const columnCount = Math.floor(autoSizerWidth / 248)
    const index = columnCount * rowIndex + columnIndex
    const photo = photos[index]
    if (photo) {
      return (
        <div style={{ margin: '0 auto', ...style }} key={index}>
          <Photo
            style={{ margin: 0 }}
            photo={photo}
            canCheck
            readyToCheck={
              !!Object.keys(checkedPhotos).length ||
              !!Object.keys(unCheckedPhotos).length ||
              checkAll
            }
            onPreview={() => viewerRef.current?.view(index)}
            checked={
              checkedPhotos[photo.seq] !== undefined ||
              (checkAll && unCheckedPhotos[photo.seq] === undefined)
            }
            onCheck={handlePushCheckedPhoto}
            onUnCheck={handleRemoveCheckedPhoto}
          />
        </div>
      )
    } else {
      return null
    }
  }

  const onCheckAll: CheckboxProps['onChange'] = (e) => {
    handleSensorOfClickEvent('chooseAll')

    dispatch({
      type: actionTypes.SET_CHECK_ALL,
      data: e.target.checked
    })
    dispatch({ type: actionTypes.CLEAR_CHECKED_PHOTO })
    dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })
  }

  let checkedCount = Object.keys(checkedPhotos).length

  if (checkAll) {
    checkedCount = photos.length - Object.keys(unCheckedPhotos).length
  }

  return (
    <>
      <div className="photoList">
        {photos.length > 0 && (
          <div
            style={{
              marginTop: 24,
              marginBottom: 12
            }}
          >
            <Checkbox
              style={{ marginRight: 10 }}
              disabled={photos.length === 0}
              indeterminate={
                (checkAll && Object.keys(unCheckedPhotos).length > 0) ||
                (!checkAll && Object.keys(checkedPhotos).length > 0)
              }
              checked={checkAll}
              onChange={onCheckAll}
            >
              全选
            </Checkbox>

            <span>
              已选择 <strong>{formatNum(checkedCount)}</strong> 张照片
              {checkedCount > 0 && (
                <span
                  style={{
                    color: '#0093FF',
                    cursor: 'pointer',
                    marginLeft: 8
                  }}
                  onClick={() => {
                    dispatch({
                      type: actionTypes.CLEAR_CHECKED_PHOTO
                    })
                    dispatch({
                      type: actionTypes.SET_CHECK_ALL,
                      data: false
                    })
                  }}
                >
                  取消选择
                </span>
              )}
            </span>
          </div>
        )}

        <AutoSizer onResize={({ width }) => setAutoSizerWidth(width)}>
          {({ width, height }) => {
            const columnCount = Math.floor(width / 248)
            const rowCount = Math.ceil(photos.length / columnCount)
            return (
              <Grid
                className="virtualized-grid"
                ref={photoListRef}
                style={{ outline: 'none', scrollBehavior: 'smooth' }}
                width={width}
                height={height}
                columnWidth={248}
                rowHeight={310}
                columnCount={columnCount}
                rowCount={rowCount}
                cellRenderer={({ columnIndex, key, rowIndex, style }) =>
                  renderCell({
                    autoSizerWidth: width,
                    columnIndex,
                    key,
                    rowIndex,
                    style
                  })
                }
                onScroll={handleGridScroll}
              />
            )
          }}
        </AutoSizer>
        <div
          className="isTop"
          style={{ opacity: visibleBackTop ? '1' : '0', right: '56px' }}
        >
          <img
            style={{ width: '48px' }}
            src={backTop}
            onClick={handleBackTopClick}
            alt=""
          />
        </div>
        {!photos.length && (
          <div className="empty-wrapper">
            <Empty description="无照片" />
          </div>
        )}
      </div>
      <Viewer
        ref={viewerRef}
        canDelete={false}
        isHideWatermarkInfo={true}
        photos={photos}
        onFetchMore={handleFetchMore}
        canFavorite={canFavorite}
        fromPlace={'photoAlbums'}
        afterFavoriteChange={(seq, favorite) => {
          dispatch(
            setPhotos(
              photos.map((photo) =>
                photo.seq === seq ? { ...photo, isFavorite: favorite } : photo
              )
            )
          )
        }}
      />
    </>
  )
}

export default PhotosList
